import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useRef, useCallback } from "react";
import { BrandColours } from "../lib/brandColours";
import { round } from "../utils/commonFunc";
import { Form, FormGroup, Tooltip, OverlayTrigger, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import NotificationAlert from "./NotificationAlert";

const acceptableFileTypes = [
  "image",
  "doc",
  "docx",
  "office",
  "pdf",
  "text",
  "txt",
];

const TEN_MEGABYTES = 10 * 1024 * 1024;

const isFileAcceptable = (file) =>
  file.size < TEN_MEGABYTES &&
  acceptableFileTypes.some((substring) => file.type.includes(substring));

const FileUploadComp = ({ onSelectedFiles }) => {
  const target = useRef(null);
  const { t } = useTranslation();

  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [filesInfo, setFilesInfo] = useState("");
  const [rejectedFilesInfo, setRejectedFilesInfo] = useState("");

  const onDrop = useCallback(
    (droppedFiles) => {
      const acceptedFilesArr = [];
      const rejectedFilesArr = [];

      for (const file of droppedFiles) {
        if (isFileAcceptable(file) && acceptedFilesArr.length < 10) {
          acceptedFilesArr.push(file);
        } else {
          rejectedFilesArr.push(file);
        }
      }

      setAcceptedFiles(acceptedFilesArr);
      setRejectedFilesInfo(rejectedFilesArr.map((file) => file.name).join(", "));

      setFilesInfo(
        acceptedFilesArr
          .map((file) => `${file.name} (${round(file.size / 1024, 1)}kb)`)
          .join(", ")
      );

      onSelectedFiles(acceptedFilesArr);
    },
    [onSelectedFiles]
  );

  // Destructuring getRootProps, getInputProps, and isDragActive from useDropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.doc', '.docx'],
      'application/pdf': ['.pdf'],
      'image/*': [],
      'text/plain': ['.txt'],
    },
    maxSize: TEN_MEGABYTES,
    multiple: true,
  });

  const toolTipAttachment = (props) => (
    <Tooltip id="tooltip-attachment" {...props}>
      {t("toolTipAttachment")}
    </Tooltip>
  );

  const handleClearFiles = () => {
    setAcceptedFiles([]);
    setFilesInfo("");
    setRejectedFilesInfo("");
    onSelectedFiles([]); // Notify parent component about the cleared files
  };

  return (
    <FormGroup style={{ fontFamily: 'HNforTELUSSA, sans-serif' }}>
      <Form.Label>
        {t("attachDoc")}
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={toolTipAttachment}
          trigger={["hover", "focus", "click"]}
        >
          <FontAwesomeIcon
            icon={faInfoCircle}
            size="lg"
            color={BrandColours.purpleEminence}
            ref={target}
          />
        </OverlayTrigger>
        <br />
        {t("attachDocSecondary")}
      </Form.Label>

      {/* Drag and Drop Area */}
      <div
        {...getRootProps({
          className: `dropzone ${isDragActive ? "drag-active" : ""} dropzone-text-style`,
        })}
      >
        <input {...getInputProps()} />
        {t("dragDrop")}
      </div>


      {filesInfo && <p className="mt-2 text-help">{filesInfo}</p>}

      {rejectedFilesInfo && (
        <div className="mt-3">
          <NotificationAlert
            variant={"danger"}
            message={`${t("badFilesText")} ${rejectedFilesInfo}`}
            onClose={() => setRejectedFilesInfo("")}
          />
        </div>
      )}

      {/* Button to Clear All Selected Files */}
      {acceptedFiles.length > 0 && (
        <Button variant="danger" onClick={handleClearFiles} className="mt-3"  style={{ fontFamily: "HNforTELUSSA400normal"}}>
          {t("clearAttachments")}
        </Button>
      )}
    </FormGroup>
  );
};

export default FileUploadComp;
